<template>
  <div class="creator-account-renewal-dialog">
    <v-dialog
      v-model="dialogVisible"
      persistent
      :content-class="'create_new_account_modal creator_account_renewal_dialog'"
      max-width="550"
    >
      <!-- Step 1: Account Issue Dialog -->
      <div v-if="currentStep === 1">
        <div class="heading">
          <h2 class="heading_new_account">
            Account Issue
          </h2>
        </div>
        <div class="wrapper_content h165">
          <div class="account_issue_box" v-if="!hasExpiresStatus(account)">
            <div class="warning-message">
              Your subscription for <a :href="getProfileUrl()" target="_blank" class="account-name-link">{{ account && (account.name || account.title) }}</a> has expired. Please renew your subscription to resolve the issue.
            </div>
          </div>
          <div class="account_issue_box" v-if="hasExpiresStatus(account)">
            <div class="warning-message">
              Please renew your <a :href="getProfileUrl()" target="_blank" class="account-name-link">{{ account && (account.name || account.title) }}</a> account to continue.
            </div>
          </div>
        </div>
        <div class="wrapper_button center">
          <button type="button" class="tetriatary_btn" v-ripple @click="cancel">Back</button>
          <button type="button" class="tetriatary_btn" v-ripple @click="startRenewal" :class="{loading: isLoading}" :disabled="isLoading">
            <v-progress-circular :size="20" :width="2" color="white" indeterminate></v-progress-circular>
            <span>Renew</span>
          </button>
        </div>
      </div>

      <!-- Step 2: Payment Form -->
      <form id="payment-form" v-if="currentStep === 2">
        <div class="heading">
          <h2 class="heading_new_account">
            {{ isRecordLabel() ? 'Record Label Account' : 'Artist Account' }}
          </h2>
        </div>
        <div class="wrapper_content payment-form-wrapper">
          <div class="info_card_box">
            <div class="account_data" v-if="!isLoading">
              <div class="description_info">
                <div class="image">
                  <img 
                    v-if="account && ((account.avatar && account.avatar.thumbnail) || (account.logo && account.logo.thumbnail))"
                    :src="getAccountImageUrl()"
                    :srcset="getAccountImageSrcset()"
                    :alt="account && (account.name || account.title)"
                    style="width: 100%; height: 100%; object-fit: cover;"
                    @error="handleImageError"
                  />
                  <div v-else class="default_user_avatar"></div>
                </div>
                <div class="name_type">
                  <div class="name">{{ account && (account.name || account.title) }}</div>
                  <div class="type">{{ isRecordLabel() ? 'Record Label' : 'Artist' }}</div>
                </div>
              </div>
              <div class="price_info">
                <!-- Record Label prices -->
                <template v-if="isRecordLabel() && selectedSubscription === 'monthly'">
                  ${{ commissionRates.record_label_subscription_month }}/mo
                </template>
                <template v-if="isRecordLabel() && selectedSubscription === 'yearly'">
                  ${{ (commissionRates.record_label_subscription_year / 12).toFixed(2) }}/mo
                </template>
                
                <!-- Artist prices -->
                <template v-if="!isRecordLabel() && selectedSubscription === 'monthly'">
                  ${{ commissionRates.artist_subscription_month }}/mo
                </template>
                <template v-if="!isRecordLabel() && selectedSubscription === 'yearly'">
                  ${{ (commissionRates.artist_subscription_year / 12).toFixed(2) }}/mo
                </template>
              </div>
            </div>
            <div class="card_data">
              <StripeElementPayment
                v-if="stripeOptions.clientSecret"
                ref="paymentRef"
                :pk="stripeToken"
                :elements-options="stripeOptions"
                :confirm-params="confirmParams"
                :redirect="'if_required'"
                :load-complete="handleElementReady"
                @error="submitPaymentCallback"
                @success="submitPaymentCallback"
              ></StripeElementPayment>
              <div v-if="!stripeOptions.clientSecret" class="loading-indicator">
                <span>Loading payment form...</span>
              </div>
            </div>
          </div>
        </div>
        <div class="wrapper_button center">
          <button type="button" class="tetriatary_btn" v-ripple @click="cancel">Close</button>
          <button type="button" class="tetriatary_btn pay" v-ripple @click="submitPayment" :class="{loading: submitStripeLoader}" :disabled="submitStripeLoader">
            <v-progress-circular :size="20" :width="2" color="white" indeterminate></v-progress-circular>
            <span>Next</span>
          </button>
        </div>
        <div class="overlay custom_style_loader" v-if="isLoading">
          <div class="progress_border">
            <v-progress-circular
              :size="250"
              :width="38"
              bg-color="rgba(255, 255, 255, 1)"
              color="#20C4F5"
              indeterminate
            ></v-progress-circular>
          </div>
        </div>
        
        <!-- Connection Error Modal -->
        <v-dialog v-model="showConnectionErrorModal" max-width="400" persistent>
          <div class="connection-error-modal">
            <div class="heading">
              <h2 class="heading_new_account">Connection Issue</h2>
            </div>
            <div class="wrapper_content">
              <div class="warning-message">
                <p>Unable to connect to the real-time update server at:</p>
                <p class="server-url">{{ centrifugeUrl || 'WebSocket Server' }}</p>
                <p>Your payment may have been processed successfully, but we couldn't verify it in real-time.</p>
              </div>
              <div class="error-details" v-if="connectionErrorDetails">
                <p class="error-title">Error details:</p>
                <p class="error-message">{{ connectionErrorDetails }}</p>
              </div>
              <div class="options-text">
                What would you like to do?
              </div>
            </div>
            <div class="wrapper_button center">
              <button type="button" class="tetriatary_btn" v-ripple @click="retryConnection">
                Try Again ({{ connectionErrorRetryCount }})
              </button>
              <button type="button" class="tetriatary_btn" v-ripple @click="proceedToSuccess">
                Proceed
              </button>
            </div>
          </div>
        </v-dialog>
      </form>

      <!-- Step 3: Success Message -->
      <div v-if="currentStep === 3">
        <div class="heading">
          <h2 class="heading_new_account">
            Subscription Renewed
          </h2>
        </div>
        <div class="wrapper_content h450">
          <div class="info_card_box">
            <div class="card_data">
              <div class="success_status">
                <div class="status">Success!</div>
                <div class="image_status"></div>
                <div class="description_status">
                  Your subscription for <span class="account-name">{{ account && (account.name || account.title) }}</span> has been successfully renewed.
                  <div class="subscription-note">Manage subscriptions in <i>Account Settings → Subscriptions</i></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="wrapper_button center">
          <button type="button" class="tetriatary_btn _button" v-ripple @click="complete">Done</button>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { StripeElementPayment } from '@vue-stripe/vue-stripe';

export default {
  name: 'CreatorAccountRenewalDialog',
  components: {
    StripeElementPayment
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    account: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      dialogVisible: false,
      currentStep: 1,
      isLoading: false,
      submitStripeLoader: false,
      paymentTimeoutId: null,
      selectedSubscription: 'monthly',
      accountPhotoUrl: null,
      activeSubscriptions: {},
      showConnectionErrorModal: false,
      connectionErrorRetryCount: 0,
      centrifugeCallback: null,
      centrifugeSelf: null,
      centrifugeClient: null,
      centrifugeWs: null,
      connectionErrorDetails: null,
      centrifugeUrl: process.env.VUE_APP_CENTRIFUGE_URL || '',
      apiUrl: process.env.VUE_APP_API_URL,
      stripeToken: process.env.VUE_APP_STRIPE_API_TOKEN,
      stripeReady: false,
      stripeOptions: {
        clientSecret: '',
        appearance: {
          variables: {
            fontSizeBase: '16px',
            colorText: '#ffffff',
            colorDanger: '#E94E4E',
            borderRadius: '5px',
            colorBackground: '#0C1324',
            colorIconTabSelected: '#ffffff',
          },
          rules: {
            '.Tab': {
              padding: '12px 12px 7px 12px',
              border: 'none',
              backgroundColor: '#182034',
              boxShadow: 'none'
            },
            '.Tab--selected, .Tab--selected:focus': {
              border: '1px solid #5F6986',
              boxShadow: 'none',
            },
            '.TabLabel': {
              color: '#ffffff',
            },
            '.Input': {
              boxShadow: 'none',
            },
            '.Input--invalid': {
              boxShadow: 'none',
              border: '1px solid #E94E4E'
            },
            '.Input:focus': {
              boxShadow: 'none',
              border: '1px solid #5F6986'
            },
            '.Input::placeholder': {
              color: '#7481A6',
            },
            '.Error': {
              fontSize: '12px',
              marginLeft: '5px'
            }
          }
        }
      },
      confirmParams: {
        return_url: `${window.location.origin}/payment-success`
      },
      submitStripeLoader: false
    }
  },
  computed: {
    ...mapGetters({
      SUBSCRIPTION_CLIENT_SECRET: 'SUBSCRIPTION_CLIENT_SECRET',
      RECORD_LABEL_CLIENT_SECRET: 'RECORD_LABEL_CLIENT_SECRET',
      commissionRates: 'COMMISSION_RATES',
      authData: 'AUTH_DATA',
      centrifugeClient: 'CENTRIFUGE_CLIENT'
    }),
    dialogVisible: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      }
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        this.dialogVisible = newValue;
      }
    },
    account: {
      immediate: true,
      handler(account) {
        if (account && (account.avatar || account.logo)) {
          const contentUrl = account.avatar?.contentUrl || account.logo?.contentUrl;
          if (contentUrl) {
            // Check if the URL is already absolute (starts with http:// or https://)
            if (contentUrl.startsWith('http://') || contentUrl.startsWith('https://')) {
              this.accountPhotoUrl = contentUrl;
            } else {
              // Otherwise, prepend the API URL
              this.accountPhotoUrl = this.apiUrl + (contentUrl.startsWith('/') ? contentUrl.slice(1) : contentUrl);
            }
            
            // Force a re-render of the component
            this.$nextTick(() => {
              this.$forceUpdate();
            });
          }
        }
      }
    },
    SUBSCRIPTION_CLIENT_SECRET: {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          this.stripeOptions.clientSecret = newValue;
          
          // Force re-render of Stripe Elements when client secret changes
          this.$nextTick(() => {
            if (this.$refs.paymentRef) {
              this.$refs.paymentRef.$forceUpdate();
            }
          });
        }
      }
    },
    'stripeOptions.clientSecret': {
      handler(newValue) {
        // Handler needed for reactivity, but no logging needed
      }
    },
    currentStep(newStep) {
      if (newStep === 2) {
        // When moving to payment step, ensure Stripe Elements are initialized
        this.$nextTick(() => {
          if (this.$refs.paymentRef && this.stripeOptions.clientSecret) {
            this.$refs.paymentRef.$forceUpdate();
          }
        });
      }
    }
  },
  mounted() {
    // Initialize Stripe token from environment variable
    this.stripeToken = process.env.VUE_APP_STRIPE_API_TOKEN;
    this.apiUrl = process.env.VUE_APP_API_URL;
    
    // Load commission rates
    this.getCommissionRates()
      .catch(err => {
        console.error(`getCommissionRates, ${err}`);
      });
    
    // Add event listener for when user navigates away from the page
    window.addEventListener('beforeunload', this.handlePageUnload);
  },
  methods: {
    // Handle page unload event (user navigating away)
    handlePageUnload() {
      // Clean up WebSocket connections when user navigates away
      this.cleanupCentrifugeSubscriptions();
    },
    
    
    getProfileUrl() {
      if (!this.account) return '#';
      
      // Link to the appropriate profile based on account type
      if (this.isRecordLabel()) {
        // Use the correct URL format for record labels with name instead of ID
        const labelName = this.account.title || this.account.name;
        return `/record-label/${labelName}`;
      } else {
        return `/profile/${this.account.id}`;
      }
    },
    
    getAccountImageUrl() {
      if (!this.account) return '';
      
      if (this.isRecordLabel() && this.account.logo && this.account.logo.thumbnail) {
        return this.account.logo.thumbnail['50x50'];
      } else if (this.account.avatar && this.account.avatar.thumbnail) {
        return this.account.avatar.thumbnail['50x50'];
      }
      
      return '';
    },
    
    getAccountImageSrcset() {
      if (!this.account) return '';
      
      if (this.isRecordLabel() && this.account.logo && this.account.logo.thumbnail) {
        return `${this.account.logo.thumbnail['50x50']} 1x, ${this.account.logo.thumbnail['50x50@2x']} 2x`;
      } else if (this.account.avatar && this.account.avatar.thumbnail) {
        return `${this.account.avatar.thumbnail['50x50']} 1x, ${this.account.avatar.thumbnail['50x50@2x']} 2x`;
      }
      
      return '';
    },
    ...mapActions({
      unCancelArtistSubscription: 'UNCANCEL_ARTIST_SUBSCRIPTION',
      renewArtistSubscription: 'RENEW_ARTIST_SUBSCRIPTION',
      unCancelRecordLabelSubscription: 'UNCANCEL_RECORD_LABEL_SUBSCRIPTION',
      renewRecordLabelSubscription: 'RENEW_RECORD_LABEL_SUBSCRIPTION',
      getCommissionRates: 'GET_COMMISSION_RATES',
      getCentrifuge: 'GET_CENTRIFUGE_CLIENT'
    }),
    
    isRecordLabel() {
      // Debug account type detection
      console.log('Account object in renewal dialog:', this.account);
      console.log('Account @type:', this.account?.['@type']);
      
      // Force record label detection for accounts in the RecordLabelSelector component
      if (this.account) {
        // Explicit check for title property which is only on record labels
        if (this.account.title) {
          console.log('Detected as record label (has title property)');
          return true;
        }
        
        // Explicit check for '@type' property
        if (this.account['@type'] === 'Label') {
          console.log('Detected as record label (@type is Label)');
          return true;
        }
      }
      
      console.log('Not detected as record label');
      return false;
    },
    
    hasExpiresStatus(account) {
      if (!account) return false;
      return account.subscription && account.subscription.status && account.subscription.status === 'past_due';
    },
    
    cancel() {
      // Reset the dialog state
      this.currentStep = 1;
      this.isLoading = false;
      this.errorMessage = '';
      this.paymentStatus = '';
      this.paymentError = '';
      this.stripeOptions.clientSecret = null;
      
      // Clean up WebSocket connections before closing dialog
      this.cleanupCentrifugeSubscriptions();
      
      this.dialogVisible = false;
      
      // Emit cancel event
      this.$emit('cancel');
    },
    
    startRenewal() {
      console.log('Starting renewal process for account:', this.account);
      this.isLoading = true;
      
      // Debug account type
      const isLabel = this.isRecordLabel();
      console.log('Is this a record label?', isLabel);
      
      // Set account photo URL if available
      if (this.account && (this.account.avatar || this.account.logo)) {
        const contentUrl = this.account.avatar?.contentUrl || this.account.logo?.contentUrl;
        if (contentUrl) {
          // Check if the URL is already absolute (starts with http:// or https://)
          if (contentUrl.startsWith('http://') || contentUrl.startsWith('https://')) {
            this.accountPhotoUrl = contentUrl;
          } else {
            // Otherwise, prepend the API URL
            this.accountPhotoUrl = this.apiUrl + (contentUrl.startsWith('/') ? contentUrl.slice(1) : contentUrl);
          }
        }
      }
      
      // Handle un-cancel subscription
      if (this.account.subscription?.issue?.type === 'cancel_expires') {
        const params = {
          subscriptionID: this.account.subscription?.id,
        };
        
        if (this.isRecordLabel()) {
          params.labelID = this.account.id;
          this.unCancelRecordLabelSubscription(params)
            .then(() => {
              this.account.hasActiveSubscription = true;
              this.account.subscription.status = 'active';
              this.account.subscription.issue = {};
              this.currentStep = 3; // Success step
            })
            .catch(err => {
              console.error(`unCancelRecordLabelSubscription, ${err}`);
            })
            .finally(() => {
              this.isLoading = false;
            });
        } else {
          params.artistID = this.account.id;
          this.unCancelArtistSubscription(params)
            .then(() => {
              this.account.hasActiveSubscription = true;
              this.account.subscription.status = 'active';
              this.account.subscription.issue = {};
              this.currentStep = 3; // Success step
            })
            .catch(err => {
              console.error(`unCancelArtistSubscription, ${err}`);
            })
            .finally(() => {
              this.isLoading = false;
            });
        }
      } else {
        // Regular renewal
        console.log('Starting regular renewal, account type:', this.isRecordLabel() ? 'Label' : 'Artist');
        
        if (this.isRecordLabel()) {
          // Record label renewal logic - following the pattern from newAccount.vue
          const params = {
            labelID: this.account.id,
            subscriptionID: this.account.subscription?.id
          };
          console.log('RECORD LABEL RENEWAL: Using params:', params);
          
          // Call the record label renewal action
          this.renewRecordLabelSubscription(params)
            .then(() => {
              console.log('Record label renewal API call successful');
              console.log('Record label client secret:', this.RECORD_LABEL_CLIENT_SECRET || 'No client secret found');
              
              if (this.RECORD_LABEL_CLIENT_SECRET) {
                this.stripeOptions.clientSecret = this.RECORD_LABEL_CLIENT_SECRET;
                this.currentStep = 2; // Move to payment step
                
                // Set up Centrifuge for real-time updates
                this.$nextTick(() => {
                  setTimeout(() => {
                    this.setupCentrifugeListener();
                  }, 100);
                });
              }
            })
            .catch(err => {
              console.error(`Record label renewal failed: ${err}`);
            })
            .finally(() => {
              this.isLoading = false;
            });

          // Exit early since we've handled the label renewal
          return;
        }
        
        // Artist renewal logic
        const params = {
          artistID: this.account.id,
          subscriptionID: this.account.subscription?.id
        };
        console.log('ARTIST RENEWAL: Using params:', params);
        
        // Store the promise for artist renewal
        const renewAction = this.renewArtistSubscription(params);
        
        renewAction
          .then(() => {
            console.log('Artist renewal API call successful');
            console.log('Client secret:', this.SUBSCRIPTION_CLIENT_SECRET || 'No client secret found');
            
            if (this.SUBSCRIPTION_CLIENT_SECRET) {
              this.stripeOptions.clientSecret = this.SUBSCRIPTION_CLIENT_SECRET;
              this.currentStep = 2; // Move to payment step
              
              // Set up Centrifuge for real-time updates after a short delay
              // This ensures the component is fully mounted before trying to connect
              this.$nextTick(() => {
                setTimeout(() => {
                  this.setupCentrifugeListener();
                }, 100);
              });
            }
          })
          .catch(err => {
            console.error(`Artist renewal failed: ${err}`);
          })
          .finally(() => {
            this.isLoading = false;
          });
      }
    },
    

    
    handleImageError(error) {
      // Silent error handling for image loading failures
    },
    
    handleImageLoad() {
      // Silent handler for successful image loads
    },
    
    handleElementReady() {
      this.stripeReady = true;
    },
    
    submitPayment() {
      if (!this.$refs.paymentRef) {
        return;
      }
      
      // Ensure we're not already in a loading state
      if (this.submitStripeLoader) {
        return;
      }
      
      this.submitStripeLoader = true;
      
      // Trigger the Stripe payment submission
      this.$refs.paymentRef.submit();
      
      // Add event listeners for Stripe iframe closure
      window.addEventListener(
        "message",
        this.handleStripeMessage,
        false
      );
    },
    
    handleStripeMessage(event) {
      try {
        // Only process messages from Stripe
        if (event.origin !== "https://js.stripe.com") return;
        
        // Check if the event data is a string that can be parsed as JSON
        if (typeof event.data !== 'string') return;
        
        const data = JSON.parse(event.data);
        
        // Handle lightbox closure
        if (data.message?.action === 'stripe-frame-action-response' && 
            data.message?.payload?.nonce?.includes('CLOSE_LIGHTBOX')) {
          this.submitStripeLoader = false;
          window.removeEventListener("message", this.handleStripeMessage);
        }
        
        // Handle payment errors
        if (data.message?.action === 'stripe-controller-action-response' && 
            data.message?.payload?.actionName === "retrievePaymentIntent" && 
            data.message?.payload?.response?.object?.last_payment_error) {
          this.submitStripeLoader = false;
          window.removeEventListener("message", this.handleStripeMessage);
        }
      } catch (error) {
        console.error('Error processing Stripe message:', error);
      }
    },
    
    // Called when component is destroyed
    beforeDestroy() {
      // Clean up any timeouts to prevent memory leaks
      if (this.paymentTimeoutId) {
        clearTimeout(this.paymentTimeoutId);
        this.paymentTimeoutId = null;
      }
      
      // Clean up Centrifuge subscriptions
      this.cleanupCentrifugeSubscriptions();
      
      // Remove the beforeunload event listener
      window.removeEventListener('beforeunload', this.handlePageUnload);
    },
    
    // Clean up all Centrifuge subscriptions
    cleanupCentrifugeSubscriptions() {
      try {
        // First, remove all event listeners to prevent error callbacks from firing
        if (this.centrifugeClient && this.centrifugeClient.transport && this.centrifugeClient.transport.transport) {
          const transport = this.centrifugeClient.transport.transport;
          if (transport.ws) {
            // Remove all event listeners from the WebSocket
            transport.ws.onclose = null;
            transport.ws.onerror = null;
            transport.ws.onmessage = null;
            transport.ws.onopen = null;
          }
        }
        
        // Unsubscribe from all active subscriptions
        Object.values(this.activeSubscriptions).forEach(sub => {
          try {
            if (sub && typeof sub.unsubscribe === 'function') {
              // Remove all event listeners from the subscription
              sub.removeAllListeners();
              sub.unsubscribe();
            }
          } catch (err) {
            // Silently handle unsubscribe errors - we're cleaning up anyway
          }
        });
        
        // Clear the subscriptions object
        this.activeSubscriptions = {};
        
        // Disconnect from Centrifuge if needed
        if (this.centrifugeClient) {
          try {
            // Remove all event listeners from the client
            this.centrifugeClient.removeAllListeners();
            this.centrifugeClient.disconnect();
          } catch (err) {
            // Silently handle disconnect errors - we're cleaning up anyway
          }
          
          // Clear the client reference
          this.centrifugeClient = null;
        }
      } catch (err) {
        // Silently handle cleanup errors - we're cleaning up anyway
      }
    },
    
    // Connect to Centrifuge for real-time updates
    getCentrifuges(channel, callback) {
      // If dialog is not visible, don't create a connection
      if (!this.dialogVisible) {
        return;
      }
      
      const self = this;
      
      // Store callback and self for potential retries
      this.centrifugeCallback = callback;
      this.centrifugeSelf = self;
      
      // Use a safer approach with fallback
      try {
        // If getCentrifuge action is not available, use fallback
        if (typeof this.getCentrifuge !== 'function') {
          console.error('getCentrifuge action is not available');
          this.handleCentrifugeFailure(callback, self);
          return;
        }
        
        // Clean up any existing connections first
        this.cleanupCentrifugeSubscriptions();
        
        // Get a fresh Centrifuge client each time
        // This ensures we don't have issues with existing subscriptions
        this.getCentrifuge()
          .then((centrifugeClientFromStore) => {
            // Store the client reference
            this.centrifugeClient = centrifugeClientFromStore;
            
            // After the action completes, get the client from the store
            if (!this.centrifugeClient || typeof this.centrifugeClient.newSubscription !== 'function') {
              console.error('Centrifuge client is not properly initialized');
              this.handleCentrifugeFailure(callback, self);
              return;
            }
            
            // Only proceed if dialog is still visible
            if (!this.dialogVisible) {
              this.cleanupCentrifugeSubscriptions();
              return;
            }
            
            // Set up error handlers for the Centrifuge client
            if (this.centrifugeClient) {
              // Listen for connection errors
              this.centrifugeClient.on('error', (error) => {
                console.error('Centrifuge client error:', error);
                
                // Log more details about transport errors
                if (error && error.type === 'transport') {
                  console.error('Transport type:', error.transport);
                  if (error.error) {
                    console.error('Transport error details:', error.error);
                    if (error.error.message) {
                      console.error('Error message:', error.error.message);
                    }
                    if (error.error.code) {
                      console.error('Error code:', error.error.code);
                    }
                  }
                }
                
                this.handleCentrifugeFailure(callback, self, error);
              });
              
              // Listen for connection close events
              this.centrifugeClient.on('disconnect', (context) => {
                if (context && context.reason) {
                  console.error('Centrifuge disconnected:', context.reason);
                  // Only show the error modal if this wasn't a normal disconnect
                  if (context.reason !== 'normal' && context.reason !== 'going away') {
                    this.handleCentrifugeFailure(callback, self);
                  }
                }
              });
            }
            
            // Set up a new subscription
            this.setupCentrifugeSubscription(this.centrifugeClient, channel, callback, self);
          })
          .catch(err => {
            console.error('CentrifugeError:', err);
            this.handleCentrifugeFailure(callback, self);
          })
          .finally(() => {
            // Ensure loading state is reset if Centrifuge setup fails
            if (this.submitStripeLoader) {
              this.submitStripeLoader = false;
            }
          });
      } catch (error) {
        console.error('Unexpected error in getCentrifuges:', error);
        this.handleCentrifugeFailure(callback, self);
      }
    },
    
    // Set up the Centrifuge subscription
    setupCentrifugeSubscription(centrifugeClient, channel, callback, self) {
      try {
        const channelName = `${channel}${this.authData.userId}`;
        
        // Try to create a new subscription with error handling
        try {
          // First, try to disconnect any existing connections
          try {
            centrifugeClient.disconnect();
          } catch (disconnectError) {
            console.log('Disconnect error (expected):', disconnectError.message);
          }
          
          // Set up WebSocket error handler to catch connection failures
          // This is where we detect the 'WebSocket connection failed' error
          if (centrifugeClient.transport && centrifugeClient.transport.transport) {
            const transport = centrifugeClient.transport.transport;
            if (transport.ws) {
              // Store a reference to the WebSocket
              this.centrifugeWs = transport.ws;
              
              // Only set error handler if dialog is still visible
              if (this.dialogVisible) {
                transport.ws.onerror = (error) => {
                  // Only handle errors if dialog is still visible
                  if (this.dialogVisible) {
                    console.error('WebSocket connection error:', error);
                    this.handleCentrifugeFailure(callback, self, error);
                  }
                };
              }
            }
          }
          
          // Wait a short time to ensure clean state
          setTimeout(() => {
            try {
              // Create a new subscription
              const sub = centrifugeClient.newSubscription(channelName);
              
              // Set up events and connect
              this.setupSubscriptionEvents(sub, callback, self);
            } catch (subError) {
              console.error('Error creating subscription after disconnect:', subError);
              this.handleCentrifugeFailure(callback, self);
            }
          }, 100);
        } catch (error) {
          console.error('Error in subscription setup:', error);
          this.handleCentrifugeFailure(callback, self);
        }
      } catch (subscriptionError) {
        console.error('Error creating Centrifuge subscription:', subscriptionError);
        this.handleCentrifugeFailure(callback, self);
      }
    },
    
    // Set up events for a subscription
    setupSubscriptionEvents(sub, callback, self) {
      try {
        // Add the publication listener
        sub.on('publication', function (ctx) {
          if (ctx.data.status === 'success') {
            callback(self);
          }
          self.submitStripeLoader = false;
        });
        
        // Always try to subscribe
        try {
          sub.subscribe();
        } catch (subscribeError) {
          // If subscription fails, it might already be subscribed
          console.log('Subscription might already be active:', subscribeError.message);
        }
        
        // Always try to connect
        try {
          if (this.centrifugeClient) {
            this.centrifugeClient.connect();
          }
        } catch (connectError) {
          console.log('Connection might already be active:', connectError.message);
        }
      } catch (error) {
        console.error('Error setting up subscription events:', error);
        this.handleCentrifugeFailure(callback, self);
      }
    },
    
    // Handle Centrifuge failure - show error modal
    handleCentrifugeFailure(callback, self, error) {
      console.log('Centrifuge connection failed - showing error modal');
      // Store the callback and self for retry
      this.centrifugeCallback = callback;
      this.centrifugeSelf = self;
      
      // Extract and store error details if available
      this.connectionErrorDetails = null;
      if (error) {
        if (typeof error === 'string') {
          this.connectionErrorDetails = error;
        } else if (error.message) {
          this.connectionErrorDetails = error.message;
        } else if (error.type === 'transport' && error.error) {
          if (error.error.message) {
            this.connectionErrorDetails = `${error.transport} error: ${error.error.message}`;
          } else {
            this.connectionErrorDetails = `${error.transport} connection failed`;
          }
        } else {
          this.connectionErrorDetails = 'WebSocket connection failed';
        }
      }
      
      // Show the connection error modal
      this.showConnectionErrorModal = true;
    },
    
    // Retry the Centrifuge connection
    retryConnection() {
      // Increment retry count
      this.connectionErrorRetryCount++;
      
      // Hide the modal
      this.showConnectionErrorModal = false;
      
      // Try to get a fresh Centrifuge client
      if (typeof this.getCentrifuge === 'function' && this.centrifugeCallback && this.centrifugeSelf) {
        const channel = 'renew_artist_subscription';
        this.getCentrifuges(channel, this.centrifugeCallback);
      } else {
        console.error('Cannot retry: missing getCentrifuge, callback, or self');
        // Show the modal again after a short delay
        setTimeout(() => {
          this.showConnectionErrorModal = true;
        }, 500);
      }
    },
    
    // Proceed to success without Centrifuge
    proceedToSuccess() {
      // Hide the modal
      this.showConnectionErrorModal = false;
      
      // Move to success step
      this.handlePaymentSuccess();
    },
    
    handlePaymentSuccess() {
      // Clean up WebSocket connections before moving to success step
      this.cleanupCentrifugeSubscriptions();
      
      // Move to success step
      this.currentStep = 3;
      
      // Update the account status
      if (this.account) {
        this.account.hasActiveSubscription = true;
        if (this.account.subscription) {
          this.account.subscription.status = 'active';
        }
      }
    },
    
    submitPaymentCallback(result) {
      this.submitStripeLoader = false;
      
      // Remove the event listener to prevent memory leaks
      window.removeEventListener("message", this.handleStripeMessage);
      
      if (result.error) {
        console.error('Payment error:', result.error);
        return;
      }
      
      console.log('Payment processed via Stripe - waiting for Centrifuge confirmation');
      // We do NOT automatically handle success here - we wait for Centrifuge
      // If Centrifuge fails, the error modal will be shown
    },
    
    setupCentrifugeListener() {
      // Use the same channel as in newAccount component for subscription renewals
      const channel = 'renew_artist_subscription';
      
      // If we don't have authData, log warning but don't auto-succeed
      if (!this.authData || !this.authData.userId) {
        console.warn('authData or userId is missing - waiting for payment callback');
        return;
      }
      
      this.getCentrifuges(channel, (self) => {
        // Update account status
        if (self.account) {
          self.account.hasActiveSubscription = true;
          if (self.account.subscription) {
            self.account.subscription.status = 'active';
            self.account.subscription.issue = {};
          }
        }
        
        // Move to success step
        self.currentStep = 3;
      });
    },
    
    complete() {
      console.log('Completing subscription renewal process');
      
      // Make sure the account status is updated
      if (this.account) {
        // Update account subscription status
        this.account.hasActiveSubscription = true;
        if (this.account.subscription) {
          this.account.subscription.status = 'active';
          // Clear any subscription issues
          this.account.subscription.issue = {};
        }
        
        // If the account is in the user's artists list, update it there too
        if (this.$store.getters.YOUR_ARTISTS) {
          const artistIndex = this.$store.getters.YOUR_ARTISTS.findIndex(a => a.id === this.account.id);
          if (artistIndex !== -1) {
            // Create a new array to ensure reactivity
            const updatedArtists = [...this.$store.getters.YOUR_ARTISTS];
            updatedArtists[artistIndex] = this.account;
            
            // Update the store with the new array
            this.$store.commit('SET_YOUR_ARTISTS', updatedArtists);
            console.log('Updated artist in Vuex store');
          }
        }
      }
      
      // Reset dialog state
      this.currentStep = 1;
      
      // Clean up Centrifuge subscriptions
      this.cleanupCentrifugeSubscriptions();
      
      // Force a refresh of the user's artists in the store
      // This will ensure the account selection wizard shows the updated status
      if (typeof this.$store.dispatch === 'function') {
        console.log('Dispatching GET_YOUR_ARTISTS action to refresh artist list with owner filter');
        this.$store.dispatch('GET_YOUR_ARTISTS', {owner: this.$store.getters.userID})
          .then(() => {
            console.log('Artist list refreshed successfully');
            
            // Close the dialog and notify parent AFTER the refresh
            this.dialogVisible = false;
            this.$emit('input', false);
            this.$emit('success', this.account);
          })
          .catch(error => {
            console.error('Error refreshing artist list:', error);
            
            // Still close the dialog even if refresh fails
            this.dialogVisible = false;
            this.$emit('input', false);
            this.$emit('success', this.account);
          });
      } else {
        // If dispatch is not available, just close the dialog
        this.dialogVisible = false;
        this.$emit('input', false);
        this.$emit('success', this.account);
      }
    }
  }
}
</script>

<style scoped>
.account-name-link {
  color: #20C4F5;
  text-decoration: none;
  font-weight: 600;
  transition: color 0.2s ease;
}

.account-name-link:hover {
  text-decoration: underline;
  color: #1ba8d4;
}

.account_issue_box {
  display: flex;
  align-items: center;
  border-radius: 5px;
  padding: 12px 0;
  margin-bottom: 10px;
}

.warning-message {
  line-height: 1.6;
  color: #ffffff;
  font-size: 15px;
}
.h165 {
  height: 165px;
}

.payment-form-wrapper {
  min-height: 450px;
  height: auto;
  max-height: 70vh;
  overflow-y: auto;
}

.account_issue_box {
  color: #fff;
  font-size: 16px;
  line-height: 1.5;
  text-align: center;
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.account_issue_box span {
  font-weight: 600;
  color: #20C4F5;
}

/* Loading button styles are handled by global styles */

.overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(12, 19, 36, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.progress_border {
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Account image styling */
.description_info {
  display: flex;
  align-items: center;
}

.description_info .image {
  width: 45px;
  height: 45px;
  border-radius: 50%;
  overflow: hidden;
  margin-right: 10px;
  position: relative;
}

.description_info .image .v-image {
  width: 100%;
  height: 100%;
}

.default_ava {
  width: 100%;
  height: 100%;
  background-color: #182034;
  border-radius: 50%;
}

.name_type .name {
  font-size: 14px;
  color: #fff;
  line-height: 1;
  margin-bottom: 2px;
}

.name_type .type {
  font-size: 12px;
  color: #dcdcdc;
}

.connection-error-modal {
  background-color: #2A2F45;
  border-radius: 8px;
  padding: 20px;
  color: #ffffff;
}

.connection-error-modal .heading {
  margin-bottom: 15px;
  text-align: center;
}

.connection-error-modal .warning-message {
  margin-bottom: 15px;
  line-height: 1.5;
}

.connection-error-modal .options-text {
  margin-bottom: 20px;
  font-weight: 600;
}

.connection-error-modal .wrapper_button {
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
}

.connection-error-modal .wrapper_button button {
  margin: 0 10px;
}

.connection-error-modal .server-url {
  font-weight: bold;
  color: #20C4F5;
  word-break: break-all;
  margin: 8px 0;
}

.connection-error-modal .error-details {
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  padding: 10px;
  margin: 10px 0;
  font-size: 13px;
}

.connection-error-modal .error-title {
  font-weight: bold;
  margin-bottom: 5px;
  color: #ff9800;
}

.connection-error-modal .error-message {
  font-family: monospace;
  white-space: pre-wrap;
  word-break: break-all;
}

/* Payment form specific styles */
.payment-form-wrapper {
  min-height: 450px;
  height: auto;
  max-height: 70vh;
  overflow-y: auto;
  padding: 0;
}

.payment-form-wrapper .info_card_box {
  display: block;
  height: auto;
  padding: 0;
}

.payment-form-wrapper .card_data {
  width: 100%;
  padding: 0 20px;
  display: block;
  align-items: initial;
}

/* Success window styles */
.h450 {
  height: 450px;
}

/* Success window specific styles */
[v-if="currentStep === 3"] .info_card_box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
}

[v-if="currentStep === 3"] .card_data {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0;
}

.success_status {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 20px;
}

.success_status .status {
  font-size: 36px;
  font-weight: 600;
  color: #20C4F5;
  margin-bottom: 20px;
}

.success_status .image_status {
  width: 120px;
  height: 120px;
  background-image: url('../../assets/image/success_crystal.svg');
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  margin-bottom: 30px;
}

.success_status .description_status {
  font-size: 16px;
  line-height: 1.5;
  color: #ffffff;
  max-width: 400px;
}

.success_status .description_status .account-name {
  color: #20C4F5;
  font-weight: 600;
}

.success_status .description_status .subscription-note {
  margin-top: 15px;
  font-size: 14px;
  color: #dcdcdc;
}

._button {
  min-width: 120px;
}
</style>
